import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import apiClient from '@/api/client'
import { useUserStore } from '@/stores/user'

export interface WalletBalance {
  balance: number
  bonus_balance: number
  currency: string
}

export interface Transaction {
  id: number
  type: 'credit' | 'debit'
  amount: number
  currency: string
  balance_before: number
  balance_after: number
  balance_type: 'cash' | 'bonus'
  status: string
  notes: string | null
  created_at: string
}

export interface TransactionsMeta {
  current_page: number
  last_page: number
  per_page: number
  total: number
}

export type TransactionFilter = 'all' | 'credit' | 'debit'

export interface DepositResponse {
  transaction_id: number
  status: string
  payment_method: string
  instructions: Record<string, unknown>
}

export interface DepositLimitUsage {
  period: string
  limit: number
  deposited: number
  remaining: number
}

export interface DepositLimit {
  id: number
  period: string
  amount: number
  currency: string
  set_by_admin: boolean
  updated_at: string | null
}

export interface DepositLimitsData {
  limits: DepositLimit[]
  usage: DepositLimitUsage[]
}

export interface WithdrawalResponse {
  message: string
  transaction_id: number
  amount: number
  status: string
}

export interface PendingWithdrawal {
  id: number
  type: string
  amount: number
  currency: string
  balance_before: number
  balance_after: number
  balance_type: string
  status: string
  notes: string | null
  created_at: string
}

export const useWalletStore = defineStore('wallet', () => {
  // Balance state
  const balance = ref<WalletBalance | null>(null)
  const loadingBalance = ref(false)
  const balanceError = ref<string | null>(null)

  // Transactions state
  const transactions = ref<Transaction[]>([])
  const transactionsMeta = ref<TransactionsMeta>({ current_page: 1, last_page: 1, per_page: 10, total: 0 })
  const loadingTransactions = ref(false)
  const transactionsError = ref<string | null>(null)

  // Deposit state
  const depositResponse = ref<DepositResponse | null>(null)
  const depositLimits = ref<DepositLimitsData | null>(null)
  const loadingDeposit = ref(false)
  const depositError = ref<string | null>(null)
  const pollingDeposit = ref(false)
  let pollTimer: ReturnType<typeof setInterval> | null = null

  // Withdrawal state
  const pendingWithdrawals = ref<PendingWithdrawal[]>([])
  const loadingWithdrawal = ref(false)
  const withdrawalError = ref<string | null>(null)
  const loadingPendingWithdrawals = ref(false)

  // Filters
  const activeFilter = ref<TransactionFilter>('all')
  const dateFrom = ref<string>('')
  const dateTo = ref<string>('')

  const hasMoreTransactions = computed(() => transactionsMeta.value.current_page < transactionsMeta.value.last_page)

  const totalBalance = computed(() => {
    if (!balance.value) return 0
    return balance.value.balance + balance.value.bonus_balance
  })

  async function fetchBalance() {
    loadingBalance.value = true
    balanceError.value = null

    try {
      const response = await apiClient.get('/api/wallet/balances')
      balance.value = response.data

      // Sync to userStore so the header balance stays in sync
      const userStore = useUserStore()
      if (userStore.wallet) {
        userStore.wallet = {
          ...userStore.wallet,
          balance: response.data.balance,
          bonus_balance: response.data.bonus_balance,
          currency: response.data.currency,
        }
      }
    } catch {
      balanceError.value = 'Failed to load balance'
    } finally {
      loadingBalance.value = false
    }
  }

  async function fetchTransactions(page = 1) {
    loadingTransactions.value = true
    transactionsError.value = null

    try {
      const params: Record<string, string | number> = {
        page,
        limit: 10,
      }

      if (activeFilter.value !== 'all') {
        params.type = activeFilter.value
      }

      if (dateFrom.value) {
        params.from = dateFrom.value
      }

      if (dateTo.value) {
        params.to = dateTo.value
      }

      const response = await apiClient.get('/api/wallet/transactions', { params })

      transactions.value = response.data.data
      transactionsMeta.value = response.data.meta
    } catch {
      transactionsError.value = 'Failed to load transactions'
    } finally {
      loadingTransactions.value = false
    }
  }

  function setFilter(filter: TransactionFilter) {
    activeFilter.value = filter
    fetchTransactions(1)
  }

  function setDateRange(from: string, to: string) {
    dateFrom.value = from
    dateTo.value = to
    fetchTransactions(1)
  }

  function goToPage(page: number) {
    fetchTransactions(page)
  }

  async function initiateDeposit(amount: number, currency: string, paymentMethod: string) {
    loadingDeposit.value = true
    depositError.value = null
    depositResponse.value = null

    try {
      const response = await apiClient.post('/api/wallet/deposit', {
        amount,
        currency,
        payment_method: paymentMethod,
      })
      depositResponse.value = response.data
      return response.data as DepositResponse
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } }
      depositError.value = axiosErr.response?.data?.message || 'Failed to initiate deposit'
      throw err
    } finally {
      loadingDeposit.value = false
    }
  }

  async function fetchDepositLimits() {
    try {
      const response = await apiClient.get('/api/profile/deposit-limits')
      depositLimits.value = response.data
    } catch {
      // Silently fail — limits are informational
    }
  }

  function pollDepositStatus(transactionId: number) {
    stopDepositPolling()
    pollingDeposit.value = true

    pollTimer = setInterval(async () => {
      try {
        const response = await apiClient.get('/api/wallet/transactions', {
          params: { limit: 20 },
        })
        const txList: Transaction[] = response.data.data
        const match = txList.find((tx) => tx.id === transactionId)

        if (match && match.status !== 'pending') {
          stopDepositPolling()
          // Refresh balance after confirmed deposit
          fetchBalance()
          // Update stored deposit response status
          if (depositResponse.value && depositResponse.value.transaction_id === transactionId) {
            depositResponse.value = { ...depositResponse.value, status: match.status }
          }
        }
      } catch {
        // Polling failure — will retry on next interval
      }
    }, 5000)
  }

  function stopDepositPolling() {
    pollingDeposit.value = false
    if (pollTimer !== null) {
      clearInterval(pollTimer)
      pollTimer = null
    }
  }

  async function submitWithdrawal(
    amount: number,
    currency: string,
    paymentMethod: string,
    destinationAddress: string,
  ): Promise<WithdrawalResponse> {
    loadingWithdrawal.value = true
    withdrawalError.value = null

    try {
      const response = await apiClient.post('/api/wallet/withdraw', {
        amount,
        currency,
        destination_address: destinationAddress,
        payment_method: paymentMethod,
      })
      // Refresh balance and pending list after withdrawal
      fetchBalance()
      fetchPendingWithdrawals()
      return response.data as WithdrawalResponse
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string; errors?: string[] } } }
      withdrawalError.value = axiosErr.response?.data?.message || 'Failed to submit withdrawal'
      throw err
    } finally {
      loadingWithdrawal.value = false
    }
  }

  async function cancelWithdrawal(transactionId: number): Promise<void> {
    loadingWithdrawal.value = true
    withdrawalError.value = null

    try {
      await apiClient.post('/api/wallet/withdraw/cancel', {
        transaction_id: transactionId,
      })
      // Refresh balance and pending list after cancellation
      fetchBalance()
      fetchPendingWithdrawals()
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } }
      withdrawalError.value = axiosErr.response?.data?.message || 'Failed to cancel withdrawal'
      throw err
    } finally {
      loadingWithdrawal.value = false
    }
  }

  async function fetchPendingWithdrawals(): Promise<void> {
    loadingPendingWithdrawals.value = true

    try {
      const response = await apiClient.get('/api/wallet/transactions', {
        params: { type: 'withdrawal', limit: 50 },
      })
      const allWithdrawals: PendingWithdrawal[] = response.data.data
      pendingWithdrawals.value = allWithdrawals.filter((tx) => tx.status === 'pending')
    } catch {
      // Silently fail — pending list is supplementary
    } finally {
      loadingPendingWithdrawals.value = false
    }
  }

  // Optimistic balance update after a game round completes.
  // Applies bet deduction + payout credit instantly so the header
  // reflects the result without waiting for a second API call.
  function applyGameResult(bet: number, payout: number) {
    const delta = payout - bet
    const userStore = useUserStore()

    if (userStore.wallet) {
      userStore.wallet = {
        ...userStore.wallet,
        balance: userStore.wallet.balance + delta,
      }
    }

    if (balance.value) {
      balance.value = {
        ...balance.value,
        balance: balance.value.balance + delta,
      }
    }
  }

  // Rollback balance to a previously saved snapshot.
  // Call this when an optimistic balance debit must be undone (e.g., bet API call failed).
  function rollbackBalance(previous: WalletBalance) {
    balance.value = { ...previous }
    const userStore = useUserStore()
    if (userStore.wallet) {
      userStore.wallet = {
        ...userStore.wallet,
        balance: previous.balance,
        bonus_balance: previous.bonus_balance,
        currency: previous.currency,
      }
    }
  }

  // Update balance from WebSocket event (called by PlayerTopNav)
  function updateBalanceFromEvent(data: { balance: number; bonus_balance: number; currency: string }) {
    if (typeof data.balance !== 'number' || typeof data.bonus_balance !== 'number') {
      console.error('[wallet] WebSocket balance update rejected: non-numeric value', data)
      return
    }
    if (data.balance < 0 || data.bonus_balance < 0) {
      console.error('[wallet] WebSocket balance update rejected: negative value', data)
      return
    }
    balance.value = data
  }

  return {
    balance,
    loadingBalance,
    balanceError,
    transactions,
    transactionsMeta,
    loadingTransactions,
    transactionsError,
    activeFilter,
    dateFrom,
    dateTo,
    hasMoreTransactions,
    totalBalance,
    depositResponse,
    depositLimits,
    loadingDeposit,
    depositError,
    pollingDeposit,
    pendingWithdrawals,
    loadingWithdrawal,
    withdrawalError,
    loadingPendingWithdrawals,
    fetchBalance,
    applyGameResult,
    rollbackBalance,
    fetchTransactions,
    setFilter,
    setDateRange,
    goToPage,
    initiateDeposit,
    fetchDepositLimits,
    pollDepositStatus,
    stopDepositPolling,
    submitWithdrawal,
    cancelWithdrawal,
    fetchPendingWithdrawals,
    updateBalanceFromEvent,
  }
})
